import PropTypes from 'prop-types';
import { Box, Typography, Chip, alpha, Tooltip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { Iconify } from 'src/components';
import MetricLineChart from './MetricLineChart';

const formatCurrency = (value) => {
    if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `£${(value / 1000).toFixed(1)}K`;
    return `£${value?.toFixed(0) || 0}`;
};

export default function LostByTreatment({ data }) {
    const [view, setView] = useState('table');
    const totalLost = data.reduce((sum, t) => sum + t.count, 0);

    const rows = useMemo(() =>
        data.map((item, idx) => ({
            id: item.name || idx,
            name: item.name,
            count: item.count || 0,
            revenue: item.revenue || 0,
            lostRate: totalLost > 0 ? ((item.count / totalLost) * 100).toFixed(1) : 0,
        })),
    [data, totalLost]);

    const columns = useMemo(() => [
        {
            field: 'name',
            headerName: 'Treatment',
            flex: 1.5,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '14px', fontWeight: 500, pl: 1 }}>{params.value}</Typography>
            ),
        },
        {
            field: 'count',
            headerName: 'Lost Count',
            flex: 0.8,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    sx={{
                        bgcolor: params.value > 0 ? alpha('#ef4444', 0.12) : 'transparent',
                        color: params.value > 0 ? '#ef4444' : 'text.disabled',
                        fontWeight: 600,
                        fontSize: '12px',
                        height: 26,
                        minWidth: 40,
                    }}
                />
            ),
        },
        {
            field: 'revenue',
            headerName: 'Lost Revenue',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#ef4444' }}>
                    {formatCurrency(params.value)}
                </Typography>
            ),
        },
        {
            field: 'lostRate',
            headerName: '% of Lost',
            flex: 0.8,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Chip
                    label={`${params.value}%`}
                    size="small"
                    sx={{
                        bgcolor: alpha('#f59e0b', 0.12),
                        color: '#f59e0b',
                        fontWeight: 600,
                        fontSize: '12px',
                        height: 26,
                        minWidth: 50,
                    }}
                />
            ),
        },
    ], []);

    // Sort rows by count desc for the chart (same ordering as table default)
    const sortedRows = useMemo(() =>
        [...rows].sort((a, b) => b.count - a.count),
    [rows]);

    const chartCategories = sortedRows.map((r) => r.name);
    const chartSeries = [{ name: 'Lost Leads', data: sortedRows.map((r) => r.count), color: '#ef4444' }];

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Tooltip title="Treatments most commonly associated with lost leads." placement="bottom" arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>Lost by Treatment</Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                    </Box>
                </Tooltip>
                <ToggleButtonGroup
                    value={view}
                    exclusive
                    size="small"
                    onChange={(_, newView) => { if (newView !== null) setView(newView); }}
                >
                    <ToggleButton value="table" sx={{ px: 1.5, '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } } }}>
                        Table
                    </ToggleButton>
                    <ToggleButton value="graph" sx={{ px: 1.5, '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } } }}>
                        Graph
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
            {view === 'table' ? (
                <Box sx={{ height: 350 }}>
                    <DataGridPro
                        rows={rows}
                        columns={columns}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        pagination
                        pageSizeOptions={[5, 10]}
                        initialState={{ pagination: { paginationModel: { pageSize: 5 } }, sorting: { sortModel: [{ field: 'count', sort: 'desc' }] } }}
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-columnHeaders': { bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
                            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' },
                            '& .MuiDataGrid-cell': { borderBottom: '1px solid #f1f5f9' },
                            '& .MuiDataGrid-row:hover': { bgcolor: '#f8fafc' },
                        }}
                    />
                </Box>
            ) : (
                <Box sx={{ p: 2 }}>
                    <MetricLineChart
                        type="bar"
                        categories={chartCategories}
                        series={chartSeries}
                        height={320}
                        horizontal
                        yAxisTitle="Lost leads"
                    />
                </Box>
            )}
        </Box>
    );
}

LostByTreatment.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        count: PropTypes.number,
        revenue: PropTypes.number,
    })),
};

LostByTreatment.defaultProps = {
    data: [],
};
